import React, { useEffect, useState } from 'react'
import { GeoTopCountries } from '../../../services'
import { Globe, MapPin } from 'lucide-react'

function TopCountries() {
  const [topCountries, setTopCountries] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchTopCountries = async () => {
    try {
      setLoading(true)
      const res = await GeoTopCountries()
      setTopCountries(res)
    } catch (error) {
      console.error('Error fetching activity logs:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTopCountries()
  }, [])

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  const maxCount = Math.max(...topCountries.map(item => item.count || 0), 1)
  const total = topCountries.reduce((sum, item) => sum + (item.count || 0), 0)

  const countryColors = [
    'from-blue-500 to-blue-600',
    'from-green-500 to-green-600',
    'from-purple-500 to-purple-600',
    'from-orange-500 to-orange-600',
    'from-pink-500 to-pink-600',
    'from-indigo-500 to-indigo-600',
    'from-teal-500 to-teal-600',
    'from-red-500 to-red-600'
  ]

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 p-6 mb-6 border border-gray-100">
      <div className="flex items-center mb-6">
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 p-3 rounded-lg mr-4">
          <Globe className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-800">Top Countries</h2>
          <p className="text-sm text-gray-500">Geographic distribution of your users</p>
        </div>
      </div>

      {topCountries.length === 0 ? (
        <div className="text-center py-12">
          <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-8 h-8 text-gray-400" />
          </div>
          <p className="text-gray-500">No country data found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {topCountries.map((item, index) => {
            const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0
            const widthPercent = ((item.count / maxCount) * 100).toFixed(1)
            const gradientClass = countryColors[index % countryColors.length]
            
            return (
              <div key={index} className="group">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{getCountryFlag(item.country)}</span>
                    <span className="text-sm font-medium text-gray-700">
                      {item.country || 'Unknown'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-500">{percentage}%</span>
                    <span className="text-lg font-bold text-gray-800">
                      {item.count?.toLocaleString() || 0}
                    </span>
                  </div>
                </div>
                <div className="relative">
                  <div className="w-full bg-gray-200 rounded-full h-8 overflow-hidden">
                    <div
                      className={`bg-gradient-to-r ${gradientClass} h-full rounded-full transition-all duration-500 ease-out flex items-center px-3`}
                      style={{ width: `${widthPercent}%` }}
                    >
                      <span className="text-xs font-semibold text-white">
                        {item.count?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {topCountries.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Total Countries</span>
              <p className="text-lg font-bold text-gray-800">{topCountries.length}</p>
            </div>
            <div>
              <span className="text-gray-500">Total Activities</span>
              <p className="text-lg font-bold text-gray-800">{total.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Helper function to get country flag emoji
function getCountryFlag(countryName) {
  const countryFlags = {
    'India': '🇮🇳',
    'United States': '🇺🇸',
    'United Kingdom': '🇬🇧',
    'Canada': '🇨🇦',
    'Australia': '🇦🇺',
    'Germany': '🇩🇪',
    'France': '🇫🇷',
    'Japan': '🇯🇵',
    'China': '🇨🇳',
    'Brazil': '🇧🇷',
    'Russia': '🇷🇺',
    'Mexico': '🇲🇽',
    'Spain': '🇪🇸',
    'Italy': '🇮🇹',
    'Netherlands': '🇳🇱',
    'Singapore': '🇸🇬'
  }
  return countryFlags[countryName] || '🌍'
}

export default TopCountries